import { Alert, Divider, Stack, Typography } from '@mui/material'
import React from 'react'
import DocLink from '../../components/DocLink'

const RemovingEmployees = () => {
  return (
    <Stack gap={4} divider={<Divider />}>
      <Stack gap={1}>
        <Typography variant="title3">How to remove an Employee</Typography>
        <Alert severity="info" variant="outlined">
          Changing an employee's status requires the <strong>Personnel</strong>{' '}
          team. Site administrators always have access.
        </Alert>
        <Typography>To remove an employee:</Typography>
        <ul style={{ marginTop: 0 }}>
          <li>Open the employee's profile</li>
          <li>
            Click the <strong>Status</strong> chip <i>(e.g. 'Live')</i>
          </li>
          <li>
            Select <strong>Leaver</strong> from the dropdown.
          </li>
        </ul>
        <Typography>A popup will then appear requesting:</Typography>
        <ul style={{ marginTop: 0 }}>
          <li>Leaving Date</li>
          <li>Reason for Leaving</li>
          <li>
            <i>
              These fields support compliance, auditing, activity logs, and
              reporting.
            </i>
          </li>
        </ul>
        <Typography>
          If the employee is still assigned to upcoming shifts, AmbuNet shows
          how many assignments will be removed and asks you to confirm before
          saving. Once saved, the employee is immediately marked as a{' '}
          <strong>Leaver</strong> and hidden from the active employees list.
        </Typography>
        <Alert severity="info" variant="outlined">
          Marking an employee as <strong>Suspended</strong> works the same way
          for shifts — their upcoming shift assignments are removed after a
          confirmation — but is intended as a temporary state that can simply
          be switched back to Live.
        </Alert>
      </Stack>
      <Stack gap={1}>
        <Typography variant="title3">
          What happens when an Employee becomes a leaver
        </Typography>
        <Typography>
          Changing an employee's status to <strong>Leaver</strong> triggers
          several system events:
        </Typography>
        <ul style={{ marginTop: 0 }}>
          <li>They are removed from their upcoming shift assignments</li>
          <li>
            They no longer appear in the active employees list, scheduling, or
            assignment pickers
          </li>
          <li>
            They stop being chased for policy and SOP signatures and drop out
            of workforce compliance dashboards
          </li>
          <li>
            They no longer count towards your subscription's active employee
            limit
          </li>
          <li>
            Their historical activity remains fully preserved (ePCRs,
            incidents, audits, shifts, etc.)
          </li>
        </ul>
        <Alert severity="warning" variant="outlined">
          Marking someone as a Leaver does not by itself stop them signing in.
          Use the <strong>Login</strong> chip on their profile to disable
          login access as part of your leaver process.
        </Alert>
        <Alert severity="info" variant="outlined">
          AmbuNet retains employee information for governance and audit
          requirements, but keeps it separate from your active workforce.
        </Alert>
      </Stack>
      <Stack gap={1}>
        <Typography variant="title3">Reinstating a Leaver</Typography>
        <Typography>
          If an employee returns to your organisation, you can reactivate them
          without creating a new record.
        </Typography>
        <ul style={{ marginTop: 0 }}>
          <li>
            Go to <DocLink url="/employees" text="Employees," />
            and tick '<strong>Include Archived</strong>'.
          </li>
          <li>Select the employee you wish to reinstate</li>
          <li>
            Open their profile and change their status back to{' '}
            <strong>Live</strong>
          </li>
          <li>
            Re-enable the <strong>Login</strong> chip if it was turned off when
            they left
          </li>
        </ul>
        <Typography>
          Their account is immediately restored, and they can once again be:
        </Typography>
        <ul style={{ marginTop: 0 }}>
          <li>Assigned shifts and scheduling workflows</li>
          <li>Included in training and competency workflows</li>
          <li>
            Granted system access (a welcome email may be required if a
            password reset is needed)
          </li>
        </ul>
        <Alert severity="info" variant="outlined">
          Reinstated employees count towards your subscription's active
          employee limit again, so reinstating may need extra employee capacity
          if you are at your limit.
        </Alert>
      </Stack>
    </Stack>
  )
}

export default RemovingEmployees
